// Phase SIKA-5e/3 + 5g — Claude.ai-style chat top bar.
//
// Layout:
//   [☰ optional]   SIKA / {goalTitle} ›   [NEW]  [Open Goal Layer ↗]
//
// - Mobile menu button (☰) only renders when `onOpenMobileSidebar`
//   is provided; on desktop it's hidden via CSS (.sc2-mobile-only).
// - Pre-5g this had a History ▾ button on the left; that was retired
//   in 5g when the secondary chat sidebar took over conversation
//   navigation. `onOpenHistory` is kept as an optional prop for
//   backwards-compat but the legacy button only renders when it
//   AND `onOpenMobileSidebar` are BOTH absent. New callers should
//   leave both unset for desktop and provide `onOpenMobileSidebar`
//   for mobile.
// - Goal name is a NavLink to /sika?goal=<id> when a goal is active.
// - Open Goal Layer is a redundant CTA (more discoverable than the
//   breadcrumb for new sessions).
// - Sticky top with backdrop blur via .sc2-topbar.

import { Link } from "react-router-dom";

import { Icon } from "../../../components/icons/Icon.tsx";

export interface ChatTopBarProps {
  /** Active goal title to show in the breadcrumb. */
  goalTitle?: string | null;
  /** Active goal id for the /sika?goal=<id> deep link. */
  goalId?: string | null;
  /** Mobile hamburger handler (open secondary sidebar drawer).
   * When provided, renders the ☰ button at the left. */
  onOpenMobileSidebar?: () => void;
  /** Callback: start a new conversation. */
  onNewConversation?: () => void;
  /** Legacy: open history slide-over. Pre-5g only; unused once the
   * secondary sidebar handles history natively. */
  onOpenHistory?: () => void;
}

export function ChatTopBar({
  goalTitle = null,
  goalId = null,
  onOpenMobileSidebar,
  onNewConversation,
  onOpenHistory,
}: ChatTopBarProps): JSX.Element {
  const goalHref = goalId ? `/sika?goal=${encodeURIComponent(goalId)}` : "/sika";
  // Legacy History button: only render when no mobile-sidebar handler
  // AND a legacy handler IS provided (i.e. caller hasn't migrated yet).
  const showLegacyHistory = !onOpenMobileSidebar && !!onOpenHistory;

  return (
    <header className="sc2-topbar" data-testid="sc2-topbar">
      {onOpenMobileSidebar ? (
        <button
          type="button"
          className="sc2-topbar-btn sc2-mobile-only"
          onClick={onOpenMobileSidebar}
          data-testid="sc2-topbar-mobile-menu"
          aria-label="Open chat navigation"
        >
          <Icon name="menu" size={14} />
        </button>
      ) : null}

      {showLegacyHistory ? (
        <button
          type="button"
          className="sc2-topbar-btn"
          onClick={onOpenHistory}
          data-testid="sc2-topbar-history-btn"
          aria-label="Open conversation history"
        >
          History ▾
        </button>
      ) : null}

      <nav
        className="sc2-topbar-breadcrumb"
        aria-label="SIKA breadcrumb"
        data-testid="sc2-topbar-breadcrumb"
      >
        <span>SIKA</span>
        {goalTitle ? (
          <>
            <span className="sc2-crumb-sep" aria-hidden>
              /
            </span>
            <Link to={goalHref} data-testid="sc2-topbar-goal-link">
              {goalTitle}
            </Link>
          </>
        ) : (
          <>
            <span className="sc2-crumb-sep" aria-hidden>
              /
            </span>
            <span style={{ color: "var(--sa-fg-muted)" }}>New conversation</span>
          </>
        )}
      </nav>

      <div className="sc2-topbar-right">
        {onNewConversation ? (
          <button
            type="button"
            className="sc2-topbar-btn"
            onClick={onNewConversation}
            data-testid="sc2-topbar-new-btn"
            aria-label="Start new conversation"
          >
            <Icon name="plus" size={11} /> &nbsp;NEW
          </button>
        ) : null}
        <Link
          to={goalHref}
          className="sc2-topbar-btn"
          data-testid="sc2-topbar-goal-cta"
        >
          Open Goal Layer ↗
        </Link>
      </div>
    </header>
  );
}
